import React, { useState, useEffect } from 'react';
import { FaEdit, FaTrash } from 'react-icons/fa';
import ApiService, { initialAuthState } from '../../services/ApiService';

const BlogPage = () => {
  const [showModal, setShowModal] = useState(false);
  const [blogName, setBlogName] = useState('');
  const [description, setDescription] = useState(''); // 🆕 Added Description
  const [productName, setProductName] = useState('');
  const [webProductId, setWebProductId] = useState('');
  const [blogImage, setBlogImage] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [blogs, setBlogs] = useState([]);
  const [products, setProducts] = useState([]);
  const [editId, setEditId] = useState(null);
  const [existingImage, setExistingImage] = useState(null);
  const [existingPdf, setExistingPdf] = useState(null);

  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');

  const fetchBlogs = async () => {
    try {
      const payload = {
        companyCode: initialAuthState.companyCode,
        unitCode: initialAuthState.unitCode,
      };

      const res = await ApiService.post('/blog/getBlogDetails', payload);
      setBlogs(
        res.data.map((b) => ({
          ...b,
          blogImage: b.image,
        }))
      );
    } catch (error) {
      console.error('Error fetching blogs:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const payload = {
        companyCode: initialAuthState.companyCode,
        unitCode: initialAuthState.unitCode,
      };

      const res = await ApiService.post(
        '/website-product/getWebsiteProductDropdown',
        payload
      );
      setProducts(res.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  useEffect(() => {
    fetchBlogs();
    fetchProducts();
  }, []);

  const handleSubmit = async () => {
    const formData = new FormData();
    formData.append('title', blogName);
    formData.append('description', description); // 🆕 Send Description
    formData.append('metaTitle', metaTitle);
    formData.append('metaDescription', metaDescription);
    formData.append('webProductId', webProductId);
    formData.append('webProductName', productName);
    formData.append('companyCode', initialAuthState.companyCode);
    formData.append('unitCode', initialAuthState.unitCode);

    if (editId) formData.append('id', editId);
    if (blogImage) formData.append('photo', blogImage);
    if (pdfFile) formData.append('pdf', pdfFile);

    try {
      await ApiService.post('/blog/handleBlogDetails', formData);
      fetchBlogs();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Error saving blog:', error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await ApiService.post(
        '/blog/deleteBlogDetails',
        { id },
        {
          headers: {
            companyCode: initialAuthState.companyCode,
            unitCode: initialAuthState.unitCode,
          },
        }
      );
      fetchBlogs();
    } catch (error) {
      console.error('Error deleting blog:', error);
    }
  };

  const resetForm = () => {
    setBlogName('');
    setDescription(''); // 🆕 Reset Description
    setMetaTitle('');
    setMetaDescription('');
    setProductName('');
    setWebProductId('');
    setBlogImage(null);
    setPdfFile(null);
    setEditId(null);
    setExistingImage(null);
    setExistingPdf(null);
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text) return "";
    return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
              Blog Management
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Manage SEO blogs, meta titles, descriptions, images and PDFs.
            </p>
          </div>

          <button
            className="bg-slate-900 text-white px-5 py-2.5 rounded-xl shadow hover:bg-slate-800 transition"
            onClick={() => setShowModal(true)}
          >
            + Add Blog
          </button>
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center rounded-t-2xl">
                <h2 className="text-xl font-semibold text-slate-900">
                  {editId ? "Edit Blog" : "Add Blog"}
                </h2>

                <button
                  className="text-slate-400 hover:text-red-500 text-2xl"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                >
                  ×
                </button>
              </div>

              <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-slate-700">
                    Blog Name
                  </label>
                  <input
                    type="text"
                    className="mt-1 w-full border border-slate-300 px-4 py-2.5 rounded-xl focus:ring-2 focus:ring-slate-900 outline-none"
                    placeholder="Enter blog name"
                    value={blogName}
                    onChange={(e) => setBlogName(e.target.value)}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-slate-700">
                    Meta Title ({metaTitle.length}/60)
                  </label>
                  <input
                    type="text"
                    maxLength={60}
                    className="mt-1 w-full border border-slate-300 px-4 py-2.5 rounded-xl focus:ring-2 focus:ring-slate-900 outline-none"
                    placeholder="Advanced GPS Tracker Solutions | Way4Track"
                    value={metaTitle}
                    onChange={(e) => setMetaTitle(e.target.value)}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-slate-700">
                    Meta Description ({metaDescription.length}/160)
                  </label>
                  <textarea
                    maxLength={160}
                    className="mt-1 w-full border border-slate-300 px-4 py-2.5 rounded-xl focus:ring-2 focus:ring-slate-900 outline-none resize-none"
                    rows="3"
                    placeholder="Enter SEO meta description"
                    value={metaDescription}
                    onChange={(e) => setMetaDescription(e.target.value)}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-slate-700">
                    Description
                  </label>
                  <textarea
                    className="mt-1 w-full border border-slate-300 px-4 py-2.5 rounded-xl focus:ring-2 focus:ring-slate-900 outline-none resize-none"
                    rows="4"
                    placeholder="Enter blog description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-slate-700">
                    Product Name
                  </label>
                  <select
                    className="mt-1 w-full border border-slate-300 px-4 py-2.5 rounded-xl focus:ring-2 focus:ring-slate-900 outline-none"
                    value={webProductId}
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      const selectedProduct = products.find(
                        (p) => p.id.toString() === selectedId
                      );

                      if (selectedProduct) {
                        setWebProductId(selectedProduct.id);
                        setProductName(selectedProduct.name);
                      }
                    }}
                  >
                    <option value="">Select Product</option>
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700">
                    Blog Image
                  </label>

                  {existingImage && !blogImage && (
                    <img
                      src={existingImage}
                      alt="Existing"
                      className="h-20 w-20 object-cover my-2 rounded-xl border"
                    />
                  )}

                  <input
                    type="file"
                    accept="image/*"
                    className="mt-2 w-full text-sm"
                    onChange={(e) => setBlogImage(e.target.files[0])}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700">
                    PDF File
                  </label>

                  {existingPdf && !pdfFile && (
                    <a
                      href={existingPdf}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block mt-2 text-blue-600 text-sm hover:underline"
                    >
                      View existing PDF
                    </a>
                  )}

                  <input
                    type="file"
                    accept="application/pdf"
                    className="mt-2 w-full text-sm"
                    onChange={(e) => setPdfFile(e.target.files[0])}
                  />
                </div>
              </div>

              <div className="border-t px-6 py-4 flex justify-end gap-3">
                <button
                  className="px-5 py-2.5 rounded-xl border text-slate-700 hover:bg-slate-100"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                >
                  Cancel
                </button>

                <button
                  onClick={handleSubmit}
                  className="px-6 py-2.5 rounded-xl bg-green-600 text-white hover:bg-green-700 shadow"
                >
                  {editId ? "Update Blog" : "Save Blog"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Desktop Table */}
        <div className="hidden md:block bg-white rounded-2xl shadow border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-white">
              <tr>
                <th className="px-4 py-3 text-left">S.No</th>
                <th className="px-4 py-3 text-left">Blog</th>
                {/* <th className="px-4 py-3 text-left">Meta Title</th>
                <th className="px-4 py-3 text-left">Meta Description</th> */}
                <th className="px-4 py-3 text-left">Product</th>
                <th className="px-4 py-3 text-center">Image</th>
                <th className="px-4 py-3 text-center">PDF</th>
                <th className="px-4 py-3 text-center">Action</th>
              </tr>
            </thead>

            <tbody>
              {blogs.map((blog, index) => (
                <tr key={blog.id} className="border-b hover:bg-slate-50">
                  <td className="px-4 py-3">{index + 1}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">
                    {blog.title}
                  </td>
                  {/* <td className="px-4 py-3">
                    {truncateText(blog.metaTitle, 45)}
                  </td>
                  <td className="px-4 py-3">
                    {truncateText(blog.metaDescription, 70)}
                  </td> */}
                  <td className="px-4 py-3">{blog.webProductName}</td>

                  <td className="px-4 py-3 text-center">
                    {blog.blogImage && (
                      <img
                        src={blog.blogImage}
                        alt="Blog"
                        className="h-12 w-12 mx-auto object-cover rounded-xl border"
                      />
                    )}
                  </td>

                  <td className="px-4 py-3 text-center">
                    {blog.pdfFile && (
                      <a
                        href={blog.pdfFile}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        View
                      </a>
                    )}
                  </td>

                  <td className="px-4 py-3 text-center">
                    <div className="flex justify-center gap-3">
                      <button
                        className="text-blue-600 hover:text-blue-800"
                        onClick={() => {
                          setBlogName(blog.title);
                          setDescription(blog.description || "");
                          setMetaTitle(blog.metaTitle || "");
                          setMetaDescription(blog.metaDescription || "");
                          setProductName(blog.webProductName);

                          const product = products.find(
                            (p) => p.name === blog.webProductName
                          );

                          setWebProductId(product ? product.id : "");
                          setEditId(blog.id);
                          setExistingImage(blog.image || null);
                          setExistingPdf(blog.pdfFile || null);
                          setShowModal(true);
                        }}
                      >
                        <FaEdit />
                      </button>

                      <button
                        className="text-red-600 hover:text-red-800"
                        onClick={() => {
                          if (window.confirm("Are you sure you want to delete this blog?")) {
                            handleDelete(blog.id);
                          }
                        }}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {blogs.length === 0 && (
            <div className="py-10 text-center text-slate-500">
              No blogs found.
            </div>
          )}
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden space-y-4">
          {blogs.map((blog) => (
            <div key={blog.id} className="bg-white rounded-2xl shadow border p-4">
              <div className="flex gap-4">
                {blog.blogImage && (
                  <img
                    src={blog.blogImage}
                    alt="Blog"
                    className="h-20 w-20 object-cover rounded-xl border"
                  />
                )}

                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900">
                    {blog.title}
                  </h3>
                  <p className="text-xs text-slate-500 mt-1">
                    {blog.webProductName}
                  </p>
                </div>
              </div>

              <div className="mt-4 text-sm space-y-2">
                <p>
                  <span className="font-medium">Meta Title:</span>{" "}
                  {truncateText(blog.metaTitle, 60)}
                </p>

                <p>
                  <span className="font-medium">Meta Description:</span>{" "}
                  {truncateText(blog.metaDescription, 100)}
                </p>
              </div>

              <div className="flex justify-between items-center mt-4 pt-4 border-t">
                {blog.pdfFile ? (
                  <a
                    href={blog.pdfFile}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 text-sm"
                  >
                    View PDF
                  </a>
                ) : (
                  <span className="text-slate-400 text-sm">No PDF</span>
                )}

                <div className="flex gap-4">
                  <button
                    className="text-blue-600"
                    onClick={() => {
                      setBlogName(blog.title);
                      setDescription(blog.description || "");
                      setMetaTitle(blog.metaTitle || "");
                      setMetaDescription(blog.metaDescription || "");
                      setProductName(blog.webProductName);

                      const product = products.find(
                        (p) => p.name === blog.webProductName
                      );

                      setWebProductId(product ? product.id : "");
                      setEditId(blog.id);
                      setExistingImage(blog.image || null);
                      setExistingPdf(blog.pdfFile || null);
                      setShowModal(true);
                    }}
                  >
                    <FaEdit />
                  </button>

                  <button
                    className="text-red-600"
                    onClick={() => {
                      if (window.confirm("Are you sure you want to delete this blog?")) {
                        handleDelete(blog.id);
                      }
                    }}
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            </div>
          ))}

          {blogs.length === 0 && (
            <div className="bg-white rounded-2xl shadow border p-8 text-center text-slate-500">
              No blogs found.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogPage;
